import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CreateOrganizationAction,
  UpdateOrganizationAction,
  UpdatePlanAction,
  SaveBookingPageSettingsAction,
  CompleteOrganizationSetupAction,
  UpdateSetupStepAction,
  GetPublicPricingAction,
  getUserInfoFromToken,
} from "../../../../store/action";
import { useWizardForm } from "./hooks/useWizardForm";
import {
  validateStep,
  resourceFieldId,
  resourceErrorsFromResponse,
} from "./utils/validation";
import {
  SELECTED_PLAN_KEY,
  PlanLimitCode,
  PlanNameKeys,
  isPlanLimitError,
  planLimitMessage,
  pricingByPlan,
} from "../../../../constants/subscription";
import { SETUP_STEP_VIDEOS } from "../../../../constants/support";
import { notifyOrganizationSetupComplete } from "../../../../utils/organizationSetup";
import { brandingSaveError } from "../../../../utils/brandingImage";
import { notify, notifyFailure } from "../../../../utils/notify";
import { publicPagePath } from "../../../../utils/publicPagePath";
import {
  createNewResource,
  saveResources,
  deleteResource,
} from "./utils/resourceHelpers";
import {
  createNewService,
  saveServices,
  deleteService,
} from "./utils/serviceHelpers";
import {
  TOTAL_STEPS,
  WIZARD_STEPS,
  setupPath,
  stepFromSlug,
} from "./wizardSteps";

// Step components
import WelcomeStep from "./steps/WelcomeStep";
import BusinessDetailsStep from "./steps/BusinessDetailsStep";
import ResourcesStep from "./steps/ResourcesStep";
import ServicesStep from "./steps/ServicesStep";
import BookingPageStep from "./steps/BookingPageStep";

// Shared components
import WizardTopBar from "./components/WizardTopBar";
import WizardHeader from "./components/WizardHeader";
import WizardActions from "./components/WizardActions";
import WizardComplete from "../WizardComplete";

import { spinnerClass } from "../../dashboardStyles";

const ORGANIZATION_HOME = "/dashboard/organization";

/**
 * Which error marks an edit to a resource field clears. Most clear their own;
 * switching away from Person hides the email box, so its mark goes with it, and
 * the schedule fields share the two marks the week is validated under.
 */
const RESOURCE_ERRORS_CLEARED_BY = {
  type: ["email"],
  workingDays: ["days"],
  workingHours: ["hours"],
  advancedSchedule: ["days", "hours"],
  useAdvancedSchedule: ["days", "hours"],
};

/**
 * The organization setup wizard, as five pages of its own.
 *
 * It used to be a modal over the Organization tab; it is now a page per step
 * under /dashboard/organization/setup/:step, in the dashboard's content column
 * beside the sidebar like any other tab, with the plan and help in a bar across
 * the top of its card. (For a while it was a full-screen route without the
 * sidebar; owners lost their bearings, it looked like a different site.) One
 * component serves all five addresses, so the form state survives moving
 * between them.
 *
 * The URL says which step is shown; the server says how far the business has
 * got (Organization.SetupStep). A step past that is not reachable by typing its
 * address — it would show a page whose earlier steps were never saved.
 */
const OrganizationWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { step: stepSlug } = useParams();
  const steps = WIZARD_STEPS.map((step) => ({
    number: step.number,
    title: t(step.titleKey),
  }));

  const [showComplete, setShowComplete] = useState(false);
  const [furthestStep, setFurthestStep] = useState(1);
  const [activeResourceId, setActiveResourceId] = useState(null);
  const [activeServiceId, setActiveServiceId] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [headerBusy, setHeaderBusy] = useState(false);

  const {
    formData,
    setFormData,
    resumeStep,
    organizationExists,
    setOrganizationExists,
    isLoading,
    errors,
    setErrors,
    handleInputChange,
  } = useWizardForm();

  const reachableStep = Math.max(furthestStep, resumeStep);
  const requestedStep = stepFromSlug(stepSlug);
  const currentStep = requestedStep ?? 1;
  const accountEmail = getUserInfoFromToken()?.email || "";

  // The same price list the public pricing page reads, so a visitor who picked
  // a plan during a promotion sees the same discounted figure in the plan menu.
  // The subscription itself is priced server-side; this is only what is shown.
  useEffect(() => {
    let cancelled = false;
    GetPublicPricingAction()
      .then((data) => {
        if (!cancelled) setPricing(data);
      })
      // Silent: `pricingByPlan` falls back to the shipped prices, and there is
      // nothing the person filling in this form can do about it.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const quotes = pricingByPlan(pricing);

  // Steps 3 and 4 open on a form, never on an empty list: the first specialist
  // and the first service are always there to fill in. Idempotent on purpose —
  // StrictMode runs effects twice in development.
  useEffect(() => {
    if (isLoading) return;
    if (currentStep === 3) {
      setFormData((prev) =>
        prev.resources.length
          ? prev
          : { ...prev, resources: [createNewResource()] },
      );
    }
    if (currentStep === 4) {
      setFormData((prev) =>
        prev.services.length ? prev : { ...prev, services: [createNewService()] },
      );
    }
    // The booking page starts from a prepared draft rather than empty boxes:
    // a title and an introduction in the business's name, and the business
    // address as the one customers see. Only fills what is still empty.
    if (currentStep === 5) {
      setFormData((prev) => {
        const name = prev.businessName.trim();
        return {
          ...prev,
          bookingTitle:
            prev.bookingTitle || t("Dashboard.Setup.page.defaultTitle", { name }),
          heroDescription:
            prev.heroDescription ||
            t("Dashboard.Setup.page.defaultIntro", { name }),
          bookingCity: prev.bookingCity || prev.city,
          streetAddress: prev.streetAddress || prev.address,
        };
      });
    }
  }, [currentStep, isLoading]);

  // Focus a field once the render that shows it has happened — on steps 3 and
  // 4 the field that failed can belong to an item that was not open.
  useEffect(() => {
    if (!focusRequest) return;
    const element = focusRequest
      .map((key) => document.getElementById(key))
      .find(Boolean);
    setFocusRequest(null);
    if (!element) return;
    // jsdom implements neither, so both calls are guarded rather than stubbed
    // in every test that renders the wizard.
    element.scrollIntoView?.({ behavior: "smooth", block: "center" });
    element.focus?.({ preventScroll: true });
  }, [focusRequest]);

  const clearErrors = useCallback(
    (keys) => {
      setErrors((prev) => {
        if (!keys.some((key) => prev[key])) return prev;
        const next = { ...prev };
        keys.forEach((key) => delete next[key]);
        return next;
      });
    },
    [setErrors],
  );

  /**
   * Put the first field that failed on screen and in focus.
   *
   * Continue sits at the foot of the page, so the boxes that just turned red
   * can all be above the fold. The validators insert their errors in the order
   * the fields are rendered, so the first key is the topmost one. On the team
   * and services steps the item it belongs to is opened first.
   */
  const focusFirstError = (stepErrors) => {
    const keys = Object.keys(stepErrors);
    const owner = (items) =>
      items.find((item) => keys.some((key) => key.endsWith(`-${item.id}`)));
    if (currentStep === 3) {
      const resource = owner(formData.resources);
      if (resource) setActiveResourceId(resource.id);
    }
    if (currentStep === 4) {
      const service = owner(formData.services);
      if (service) setActiveServiceId(service.id);
    }
    setFocusRequest(keys);
  };

  // Resource handlers
  const handleAddResource = () => {
    const newResource = createNewResource();
    setFormData((prev) => ({
      ...prev,
      resources: [...prev.resources, newResource],
    }));
    setActiveResourceId(newResource.id);
  };

  const handleUpdateResource = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      resources: prev.resources.map((r) =>
        r.id === id ? { ...r, [field]: value } : r,
      ),
    }));

    // Same rule as step 2's handleInputChange: a mark goes the moment its
    // field is touched.
    clearErrors(
      (RESOURCE_ERRORS_CLEARED_BY[field] ?? [field]).map((name) =>
        resourceFieldId(name, id),
      ),
    );
  };

  const handleRemoveResource = async (id) => {
    try {
      await deleteResource(id);
      setFormData((prev) => ({
        ...prev,
        resources: prev.resources.filter((r) => r.id !== id),
      }));
      if (activeResourceId === id) setActiveResourceId(null);
      clearErrors(
        ["name", "email", "days", "hours"].map((name) =>
          resourceFieldId(name, id),
        ),
      );
    } catch (error) {
      console.error("Error deleting resource:", error);
      notifyFailure(error, t("Dashboard.Wizard.errors.deleteResourceFailed"));
    }
  };

  const handleJumpToResourceError = (fieldId, resourceId) => {
    setActiveResourceId(resourceId);
    setFocusRequest([fieldId]);
  };

  // Service handlers
  const handleAddService = () => {
    const newService = createNewService();
    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }));
    setActiveServiceId(newService.id);
  };

  const handleUpdateService = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((s) =>
        s.id === id ? { ...s, [field]: value } : s,
      ),
    }));
    clearErrors([`service-${field}-${id}`]);
  };

  const handleRemoveService = async (id) => {
    try {
      await deleteService(id);
      setFormData((prev) => ({
        ...prev,
        services: prev.services.filter((s) => s.id !== id),
      }));
      if (activeServiceId === id) setActiveServiceId(null);
      clearErrors([`service-name-${id}`, `service-duration-${id}`]);
    } catch (error) {
      console.error("Error deleting service:", error);
      notifyFailure(error, t("Dashboard.Wizard.errors.deleteServiceFailed"));
    }
  };

  // File upload handler for booking page
  const handleFileChange = (file, removed, fieldName) => {
    if (fieldName === "logoUrl") setLogoFile(file);
    else setHeaderImageFile(file);
    const removalField = fieldName === "logoUrl" ? "removeLogo" : "removeHeaderImage";
    setFormData((prev) => ({ ...prev, [removalField]: removed }));
    setErrors({});
  };

  /**
   * The plan menu in the top bar. Before the organization exists the choice
   * is only remembered — it rides on the create request on step 2 (and in
   * SELECTED_PLAN_KEY, so a reload before then keeps it). After, it changes
   * the subscription straight away.
   *
   * The one refusal expected is a team bigger than the free tier's single
   * specialist being moved onto it; the menu then stays on the plan the
   * business is actually on.
   */
  const handlePlanChange = async (plan) => {
    if (!organizationExists) {
      setFormData((prev) => ({ ...prev, plan }));
      localStorage.setItem(SELECTED_PLAN_KEY, String(plan));
      return;
    }

    setPlanSaving(true);
    try {
      await UpdatePlanAction(plan);
      setFormData((prev) => ({ ...prev, plan }));
      notify.success(
        t("Dashboard.Setup.plan.changed", { plan: t(PlanNameKeys[plan]) }),
      );
    } catch (error) {
      if (isPlanLimitError(error, PlanLimitCode.STAFF)) {
        notify.warning(t("Dashboard.Plan.tooManyStaffForPlan"));
      } else {
        notifyFailure(error, t("Dashboard.Setup.plan.changeFailed"));
      }
    } finally {
      setPlanSaving(false);
    }
  };

  const goToStep = (number) => {
    setErrors({});
    navigate(setupPath(number));
  };

  /** Step 2: create the organization, or update it on a second pass. */
  const saveBusinessDetails = async () => {
    const organizationData = {
      businessName: formData.businessName.trim(),
      companyName: formData.companyName.trim(),
      buyerType: Number(formData.buyerType),
      registrationCode: formData.registrationCode.trim(),
      phoneNumber: formData.phoneNumber || null,
      phoneCountryCode: formData.phoneCountryCode,
      county: formData.county.trim(),
      city: formData.city.trim(),
      address: formData.address.trim(),
    };

    // The server assigns the page's address on create and keeps it on every
    // update; the booking-page step and the last screen link to it.
    const keepSlug = (saved) => {
      if (saved?.slug) setFormData((prev) => ({ ...prev, slug: saved.slug }));
    };

    if (organizationExists) {
      keepSlug(await UpdateOrganizationAction(organizationData));
      return;
    }

    try {
      // The plan only rides on create; the server ignores it on update, and
      // from here on the plan menu changes the subscription directly.
      keepSlug(
        await CreateOrganizationAction({
          ...organizationData,
          plan: Number(formData.plan),
        }),
      );
      // The org (and its subscription) now exist; drop any Pricing-page
      // preselection so it can't leak into a later signup.
      localStorage.removeItem(SELECTED_PLAN_KEY);
    } catch (error) {
      // Created in another tab or on another device since this page loaded.
      if (error.response?.status !== 409) throw error;
      keepSlug(await UpdateOrganizationAction(organizationData));
    }
    setOrganizationExists(true);
  };

  const handleContinue = async () => {
    if (saving || logoBusy || headerBusy) return;

    const stepErrors = validateStep(currentStep, formData, t);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      focusFirstError(stepErrors);
      return;
    }
    setErrors({});

    setSaving(true);
    try {
      if (currentStep === 2) {
        try {
          await saveBusinessDetails();
        } catch (error) {
          console.error("Error saving organization details:", error);
          notifyFailure(
            error,
            t("Dashboard.Wizard.errors.saveOrganizationDetailsFailed"),
          );
          return;
        }
        notify.success(t("Dashboard.Wizard.saved.businessDetails"));
      }

      if (currentStep === 3) {
        try {
          // Merge each item's server-assigned id back as soon as it saves,
          // matched by its original client-side id — not by array position. A
          // failure partway through (the plan's seat cap) then still leaves
          // the ones that succeeded marked as saved for the retry, instead of
          // saveResources's isDbId check creating duplicates. The server's
          // userId comes back too: an owner's own email links them on create.
          await saveResources(formData.resources, (original, result) => {
            setFormData((prev) => ({
              ...prev,
              resources: prev.resources.map((r) =>
                r.id === original.id
                  ? {
                      ...r,
                      id: result?.id ?? r.id,
                      userId: result?.userId ?? r.userId ?? null,
                    }
                  : r,
              ),
            }));
            setActiveResourceId((active) =>
              active === original.id ? result?.id ?? active : active,
            );
          });
          notify.success(t("Dashboard.Wizard.saved.resources"));
        } catch (error) {
          console.error("Error saving resources:", error);
          // A 400 names the field it refused: mark it on the resource that
          // failed, exactly as the client-side check would have.
          const fieldErrors = resourceErrorsFromResponse(error, t);
          if (fieldErrors) {
            setErrors(fieldErrors);
            focusFirstError(fieldErrors);
            return;
          }
          // A 402 here is the plan refusing another specialist — a warning,
          // said with the hint that points at the plan menu rather than the
          // server's sentence.
          if (isPlanLimitError(error, PlanLimitCode.STAFF)) {
            notify.warning(t("Dashboard.Plan.staffCapHint"));
          } else {
            notifyFailure(error, t("Dashboard.Wizard.errors.saveResourcesFailed"));
          }
          return;
        }
      }

      if (currentStep === 4) {
        try {
          // Same incremental-merge reasoning as the resources step above.
          await saveServices(formData.services, (original, result) => {
            setFormData((prev) => ({
              ...prev,
              services: prev.services.map((s) =>
                s.id === original.id ? { ...s, id: result?.id ?? s.id } : s,
              ),
            }));
            setActiveServiceId((active) =>
              active === original.id ? result?.id ?? active : active,
            );
          });
          notify.success(t("Dashboard.Wizard.saved.services"));
        } catch (error) {
          console.error("Error saving services:", error);
          if (isPlanLimitError(error)) {
            notify.warning(
              planLimitMessage(error) ||
                t("Dashboard.Wizard.errors.saveServicesFailed"),
            );
          } else {
            notifyFailure(error, t("Dashboard.Wizard.errors.saveServicesFailed"));
          }
          return;
        }
      }

      // Step 5: no success message — the completion screen is the
      // confirmation.
      if (currentStep === 5) {
        try {
          const settings = await SaveBookingPageSettingsAction(
            {
              // What the wizard does not edit, sent back as loaded: an omitted
              // field arrives as null and clears its column.
              ...formData.pageExtras,
              bookingTitle: formData.bookingTitle || null,
              heroDescription: formData.heroDescription || null,
              streetAddress: formData.streetAddress || null,
              bookingCity: formData.bookingCity || null,
              primaryColor: formData.primaryColor,
              secondaryColor: formData.secondaryColor,
              removeLogo: formData.removeLogo,
              removeHeaderImage: formData.removeHeaderImage,
            },
            logoFile,
            headerImageFile,
          );
          setLogoFile(null);
          setHeaderImageFile(null);
          setFormData((prev) => ({
            ...prev,
            logoUrl: settings.logoUrl || "",
            headerImageUrl: settings.headerImageUrl || "",
            removeLogo: false,
            removeHeaderImage: false,
          }));
        } catch (error) {
          console.error("Error saving booking page settings:", error);
          notifyFailure(
            error,
            brandingSaveError(
              error,
              t,
              "Dashboard.Wizard.errors.saveBookingPageSettingsFailed",
            ),
          );
          return;
        }

        // Mark the organization as set up, which unlocks the setup-gated tabs.
        // Own try/catch on purpose: every step is saved, and a failure here
        // must not block the completion screen — it is idempotent, and the
        // Organization tab offers to finish again.
        try {
          await CompleteOrganizationSetupAction();
          notifyOrganizationSetupComplete();
        } catch (error) {
          console.error("Error marking organization setup complete:", error);
        }
        setShowComplete(true);
        return;
      }

      const nextStep = currentStep + 1;
      // Record the resume position server-side (Organization.SetupStep) so the
      // wizard picks up here on any device. Only forwards: going back to fix
      // step 2 must not make a later visit forget that step 4 was reached.
      // Step 1 saves nothing, so there is no organization yet to write to.
      // Own try/catch: the user's data is saved, and losing only the resume
      // marker must not stop them moving on.
      if (currentStep >= 2 && nextStep > reachableStep) {
        try {
          await UpdateSetupStepAction(nextStep);
        } catch (error) {
          console.error("Error saving wizard resume step:", error);
        }
      }
      setFurthestStep((furthest) => Math.max(furthest, nextStep));
      goToStep(nextStep);
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  };

  const handleClose = () => navigate(ORGANIZATION_HOME);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <div
          role="status"
          aria-label={t("Dashboard.Wizard.loadingAriaLabel")}
          className={spinnerClass}
        />
        <p className="m-0 text-fluid-sm text-ink-500">
          {t("Dashboard.Wizard.loadingProgress")}
        </p>
      </div>
    );
  }

  // No step, an unknown one, or one the business has not reached yet: the
  // furthest it may open.
  if (!requestedStep || requestedStep > reachableStep) {
    return <Navigate to={setupPath(requestedStep ? reachableStep : resumeStep)} replace />;
  }

  if (showComplete) {
    return (
      <WizardComplete
        bookingUrl={publicPagePath(formData.slug)}
        onClose={handleClose}
      />
    );
  }

  const busy = saving || logoBusy || headerBusy;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep />;

      case 2:
        return (
          <BusinessDetailsStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
          />
        );

      case 3:
        return (
          <ResourcesStep
            resources={formData.resources}
            activeId={activeResourceId}
            errors={errors}
            plan={formData.plan}
            accountEmail={accountEmail}
            onSelect={setActiveResourceId}
            onJumpToError={handleJumpToResourceError}
            onAdd={handleAddResource}
            onUpdate={handleUpdateResource}
            onRemove={handleRemoveResource}
          />
        );

      case 4:
        return (
          <ServicesStep
            services={formData.services}
            activeId={activeServiceId}
            errors={errors}
            onSelect={setActiveServiceId}
            onAdd={handleAddService}
            onUpdate={handleUpdateService}
            onRemove={handleRemoveService}
          />
        );

      case 5:
        return (
          <BookingPageStep
            formData={formData}
            onChange={handleInputChange}
            onFileChange={handleFileChange}
            logoFile={logoFile}
            headerImageFile={headerImageFile}
            disabled={saving}
            onLogoBusyChange={setLogoBusy}
            onHeaderBusyChange={setHeaderBusy}
            canPreview={organizationExists}
          />
        );

      default:
        return null;
    }
  };

  const isLast = currentStep === TOTAL_STEPS;

  // Edge to edge on a phone (the -mx-4 undoes the content column's gutter): the
  // steps were laid out for that width, and a card inside the gutter would take
  // another 32px from them. A card like the dashboard's others from `sm` up.
  // No overflow-hidden: the plan menu drops out of it, and the actions bar has
  // to stick to the bottom of the window.
  return (
    <div className="-mx-4 sm:mx-0">
      <div className="mx-auto flex w-full max-w-6xl flex-col">
        <div className="flex flex-col border-y border-ink-200 bg-white sm:rounded-card sm:border sm:shadow-card">
          <WizardTopBar
            plan={formData.plan}
            quotes={quotes}
            onPlanSelect={handlePlanChange}
            planBusy={planSaving}
            onClose={handleClose}
            videoUrl={SETUP_STEP_VIDEOS[WIZARD_STEPS[currentStep - 1].slug]}
          />
          <WizardHeader
            steps={steps}
            currentStep={currentStep}
            onStepSelect={busy ? undefined : goToStep}
          />
          {/* A section, not <main>: it now sits inside the dashboard's. */}
          <section className="flex-1 px-4 pt-6 pb-8 sm:px-8 lg:px-10">
            {renderStepContent()}
          </section>
          <WizardActions
            onPrevious={currentStep > 1 ? handlePrevious : undefined}
            onContinue={handleContinue}
            continueLabel={
              currentStep === 1
                ? t("Dashboard.Setup.actions.start")
                : isLast
                  ? t("Dashboard.Setup.actions.finish")
                  : undefined
            }
            continueIcon={isLast ? "bi-chevron-right" : "bi-arrow-right"}
            hint={isLast ? t("Dashboard.Setup.page.laterHint") : undefined}
            disabled={busy}
          />
        </div>
      </div>
    </div>
  );
};

export default OrganizationWizard;
